import React, { useState, useEffect, useRef } from 'react';
import Text from 'components/Text/Text';
import { Icon, IconName } from 'components/Icon/Icon';
import classNames from 'classnames';
import style from './ChunkOfTextWithCopyingNew.module.scss';

interface IProps {
	text: string;
	className?: string;
}

export const ChunkOfTextWithCopyingNew: React.FC<IProps> = React.memo(({ text, className }) => {
	const [showIcon, setShowIcon] = useState(false);
	const timeout = useRef<null | ReturnType<typeof setTimeout>>(null);
	const containerClasses = classNames(style.container, className);
	const iconClasses = classNames(style.icon, { [style.showIcon]: showIcon });

	const chunkOfText = `...${text.slice(-4)}`;

	const showCopiedIcon = () => {
		if (!showIcon) {
			setShowIcon(true);
			timeout.current = setTimeout(() => {
				setShowIcon(false);
			}, 2000);
		}
	};

	const unsecuredCopyToClipboard = () => {
		const textArea = document.createElement('textarea');
		textArea.value = text;
		textArea.style.position = 'absolute';
		textArea.style.left = '-99999px';
		document.body.appendChild(textArea);
		textArea.focus();
		textArea.select();
		try {
			document.execCommand('copy');
			showCopiedIcon();
		} catch (error) {
			console.error('Error in copying text: ', error);
		}
		document.body.removeChild(textArea);
	};

	const handleCopyAllText = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		e.stopPropagation();
		if (window.isSecureContext && navigator.clipboard) {
			try {
				await navigator.clipboard.writeText(text); // navigator.clipboard.writeText(text) not working on insecure(http) connection
				showCopiedIcon();
			} catch (error) {
				console.error('Error in copying text: ', error);
			}
		} else {
			unsecuredCopyToClipboard();
		}
	};

	useEffect(() => {
		return () => {
			if (timeout.current) {
				clearTimeout(timeout.current);
			}
		};
	}, []);

	return (
		<div className={containerClasses}>
			<div className={style.textWrapper} onClick={handleCopyAllText}>
				<Text translationKey={chunkOfText} size="xs" theme="neutralMedium" />
				<Text className={style.fullText} translationKey={text} withTooltip={true} isNewTooltip={true} />
			</div>
			<Icon name={IconName.fileCopy} className={iconClasses} />
		</div>
	);
});
